import React from 'react';
import { Calendar, MapPin, Briefcase, Award, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';

const Experience = ({ data }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return (
    <section id="experience" className="py-24 relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/20 to-slate-900/10" />
        <div className="absolute top-20 left-20 w-64 h-64 bg-slate-400/3 rounded-full blur-3xl" />
        <div className="absolute bottom-32 right-32 w-80 h-80 bg-slate-500/2 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10" ref={ref}>
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Briefcase className="h-8 w-8 text-slate-400" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50">
              Experience
            </h2>
          </div>
          <motion.div 
            className="w-16 h-0.5 bg-slate-500 mx-auto mb-6"
            initial={{ width: 0 }}
            animate={inView ? { width: 64 } : { width: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            10+ years building thoughtful solutions across AI/ML platforms, data analytics, and enterprise systems
          </p>
        </motion.div>

        <div className="space-y-8">
          {/* Timeline Line */}
          <motion.div
            className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-600 hidden lg:block"
            initial={{ height: 0 }}
            animate={inView ? { height: "100%" } : { height: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          />
          
          {data.map((job, index) => (
            <motion.div
              key={job.id}
              variants={cardVariants}
              whileHover={{ y: -4, scale: 1.01 }}
              transition={{ duration: 0.2 }}
              className="relative"
            >
              {/* Timeline Dot */}
              <motion.div
                className="absolute left-5 top-8 w-3 h-3 bg-slate-400 rounded-full border-4 border-slate-950 hidden lg:block z-10"
                initial={{ scale: 0, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                transition={{ delay: index * 0.15 + 0.8, duration: 0.3 }}
                whileHover={{ scale: 1.3 }}
              />
              
              <div className="lg:ml-12 glass-card p-10 group">
                <motion.div 
                  className="mb-8"
                  initial={{ y: 20, opacity: 0 }}
                  animate={inView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
                  transition={{ delay: index * 0.15 + 0.4, duration: 0.6 }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-50 mb-2 leading-tight">
                        {job.position}
                      </h3>
                      <p className="text-slate-300 font-medium text-base mb-3">
                        {job.company}
                      </p>
                      <p className="text-slate-400 leading-normal text-sm">
                        {job.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-xl border border-slate-700/40 shrink-0">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-200 text-sm font-medium whitespace-nowrap">{job.duration}</span>
                    </div>
                  </div>
                </motion.div>
                <motion.div 
                  className="space-y-2"
                  initial={{ y: 20, opacity: 0 }}
                  animate={inView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
                  transition={{ delay: index * 0.15 + 0.6, duration: 0.6 }}
                >
                  {job.achievements.map((achievement, achievementIndex) => (
                    <motion.div
                      key={achievementIndex} 
                      className="flex items-start gap-3 p-3 bg-slate-800/40 rounded-lg border border-slate-700/30 hover:bg-slate-800/50 transition-colors duration-200"
                      initial={{ opacity: 0, x: -10 }}
                      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                      transition={{ delay: index * 0.1 + 0.3 + achievementIndex * 0.05, duration: 0.3 }}
                    >
                      <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                      <p className="text-slate-200 leading-snug text-sm">
                        {achievement}
                      </p>
                    </motion.div>
                  ))}
                  
                  {/* Additional Work Section for thoughtbot */}
                  {job.additionalWork && (
                    <motion.div
                      className="mt-4 pt-4 border-t border-slate-700/30"
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: index * 0.15 + 1.2, duration: 0.6 }}
                    >
                      <h4 className="text-slate-300 font-medium mb-3 text-sm tracking-wide">
                        {job.additionalWork.title}
                      </h4>
                      <div className="space-y-2">
                        {job.additionalWork.items.map((item, itemIndex) => (
                          <motion.div
                            key={itemIndex}
                            className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg border border-slate-700/25 hover:bg-slate-800/40 transition-colors duration-200"
                            initial={{ opacity: 0, x: -10 }}
                            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                            transition={{ delay: index * 0.1 + 0.6 + itemIndex * 0.03, duration: 0.3 }}
                          >
                            <div className="w-1.5 h-1.5 bg-slate-500 rounded-full mt-2 flex-shrink-0" />
                            <p className="text-slate-300 leading-snug text-sm">
                              {item}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;